import React, { FC, useEffect, useRef, useState } from "react";
import { AdyenCheckout, BrowserInfo, Card, CoreConfiguration, PaymentData } from "@adyen/adyen-web";
import { useSelector, useDispatch } from "react-redux";
import { INVOICE_TYPE, PAYMENT_METHOD } from "../../../../../../constants";
import PAGES from "../../../../../../constants/pages";
import { RootState } from "../../../../../../models";
import { AdyenPaymentDetails, CardDetails } from "../../../../../../models/actionPayloads/createPurchaseRequest";
import { getConfig as configSelector } from "../../../../app/state/selectors";
import { selectedPackageNames } from "../../../../prepaid/state/selectors";
import { checkoutPayment } from "../../../state/actions/paymentActions";

interface AdyenPaymentMethodProps {
    amountToPay: number;
    isDisabled?: boolean;
}

const AdyenPaymentMethod: FC<AdyenPaymentMethodProps> = ({ amountToPay, isDisabled }) => {
    const cardContainerRef = useRef<HTMLDivElement>(null);
    const cardComponentRef = useRef<any>(null);
    const dispatch = useDispatch();
    const adyenConfig = useSelector((state: RootState) => configSelector(state)?.adyen);
    const packageNames = useSelector(selectedPackageNames);
    const [hasInitiatedSubmit, setHasInitiatedSubmit] = useState(false);

    useEffect(() => {
        const setupAdyen = async () => {
            if (!cardContainerRef.current) {
                return;
            }

            try {
                const configuration: CoreConfiguration = {
                    clientKey: adyenConfig?.clientKey,
                    environment: adyenConfig?.environment,
                    amount: {
                        value: amountToPay * 100, // Adyen expects the amount in minor units
                        currency: "NOK",
                    },
                    locale: "no-NO",
                    countryCode: "NO",
                    onSubmit: async (state, actions) => {
                        const transactionText = packageNames.join(", ");
                        const paymentData = mapToPaymentRequest(state.data, transactionText);
                        setHasInitiatedSubmit(true);
                        const result = await dispatch(
                            checkoutPayment(
                                undefined,
                                undefined,
                                undefined,
                                PAYMENT_METHOD.ADYEN_CC,
                                INVOICE_TYPE.EMAIL,
                                paymentData,
                            ),
                        );
                        const adyenAction = result.action?.payload?.action;
                        if (adyenAction) {
                            actions.handleAction(adyenAction);
                        }
                    },
                };

                const checkout = await AdyenCheckout(configuration);

                cardComponentRef.current = new Card(checkout, {
                    hasHolderName: true,
                    holderNameRequired: true,
                    billingAddressRequired: false,
                    enableStoreDetails: false,
                });

                cardComponentRef.current.mount(cardContainerRef.current);
            } catch (error) {
                // oxlint-disable-next-line no-console
                console.error("AdyenPaymentMethod: Setup failed", error);
            }
        };

        setupAdyen();

        return () => {
            if (cardComponentRef.current) {
                cardComponentRef.current.unmount();
            }
        };
    }, [adyenConfig, packageNames]);
    useEffect(() => {
        if (cardComponentRef.current) {
            cardComponentRef.current.setStatus(hasInitiatedSubmit ? "loading" : "ready");
        }
    }, [hasInitiatedSubmit]);

    return (
        <div className="adyenPaymentCard">
            <div id="component-container" ref={cardContainerRef}></div>
            {isDisabled && <div className="adyenPaymentCard__disabled" />}
        </div>
    );
};

const mapToPaymentRequest = (paymentData: PaymentData, transactionText: string): AdyenPaymentDetails => {
    const { paymentMethod, browserInfo } = paymentData;

    const customBrowserInfo: BrowserInfo = {
        acceptHeader: browserInfo?.acceptHeader || "*/*",
        colorDepth: browserInfo?.colorDepth || 24,
        javaEnabled: browserInfo?.javaEnabled || false,
        language: browserInfo?.language || "en",
        screenHeight: browserInfo?.screenHeight || 0,
        screenWidth: browserInfo?.screenWidth || 0,
        timeZoneOffset: browserInfo?.timeZoneOffset || 0,
        userAgent: browserInfo?.userAgent || "",
    };

    const cardDetails: CardDetails = {
        cardHolderName: paymentMethod.holderName || "",
        brand: paymentMethod.brand || "",
        encryptedCardNumber: paymentMethod.encryptedCardNumber || "",
        encryptedSecurityCode: paymentMethod.encryptedSecurityCode || "",
        encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth || "",
        encryptedExpiryYear: paymentMethod.encryptedExpiryYear || "",
    };

    return {
        browserInfo: customBrowserInfo,
        cardDetails,
        origin: window.location.origin,
        returnUrl: window.location.origin + PAGES.PREPAID.REFILL.PAYMENT_PROCESSING.URL,
        transactionText,
    };
};

export default AdyenPaymentMethod;
